<template>
  <div ref="reportContent" class="report-clipboard" v-html="formattedReport" style="display: none;"></div>
</template>

<script>
export default {
  name: "ReportClipboard",
  props: {
    reportData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      internalReportData: {}
    };
  },
  computed: {
    formattedReport() {
      const {
        shift = 'Shift',
        date = 'Unknown Date',
        monitor = 'Unknown Monitor',
        headerColor = '#000000',
        satelliteShiftStart = '',
        satelliteShiftEnd = '',
        hasSecondSatelliteShift = false,
        satelliteSecondShiftStart = '',
        satelliteSecondShiftEnd = '',
        attendance = [],
        salesData = [],
        modelFocus = [],
        breakItems = [],
        adjustmentList = [],
        shiftObservations = [],
        operationalTasks = [],
        checklist = [],
        reportSites = [],
        platformTotals = {},
        grandTotal = 0,
        studio_id = undefined
      } = this.internalReportData || {};

      // Satellite detection
      const isSatellite = shift.toLowerCase() === 'satellite';
      const salesSites = reportSites.length > 0
        ? reportSites
        : [
            { displayName: 'Flirt4Free', shortLabel: 'F4F' },
            { displayName: 'Streamate', shortLabel: 'SM' },
            { displayName: 'LiveJasmin', shortLabel: 'LJ' },
            { displayName: 'Chaturbate', shortLabel: 'CB' },
            { displayName: 'StripChat', shortLabel: 'SC' },
          ];
      const useShortSiteLabels = salesSites.length > 5;

      // Try to use studio_name from reportData or user data
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const studioNameFromUser = user.studio_name;
      const studioNameFromReport = this.internalReportData.studio_name;

      // Studio mapping (fallback only; prefer dynamic names)
      const studioMap = {
        1: 'CC',
        2: 'CJ',
        3: 'BOG',
        4: 'PEI',
      };
      const studioKey = parseInt(studio_id);
      const studioLabel = isSatellite
        ? 'M Group'
        : (studioNameFromReport || studioNameFromUser || studioMap[studioKey] || `Studio ${studio_id || 'Unknown'}`);

      const capitalizeShift = shift.charAt(0).toUpperCase() + shift.slice(1);
      const headerStyle = `background-color: ${headerColor}; color: #fff; padding: 8px; font-size: 18px; font-weight: bold; text-align: center; box-sizing: border-box;`;
      const subHeaderStyle = `background-color: ${headerColor}; color: #fff; padding: 6px; font-size: 14px; font-weight: bold; text-align: left; box-sizing: border-box;`;
      const tableStyle = `width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 12px;`;
      const thStyle = `border: 1px solid #ddd; padding: 6px; background-color: #f5f5f5; text-align: left;`;
      const tdStyle = `border: 1px solid #ddd; padding: 6px; text-align: left;`;
      const focusedTdStyle = `border: 3px solid ${headerColor}; padding: 6px; text-align: left;`;
      const sectionWrapStyle = `width: 100%; max-width: 800px; margin: 0 auto; background: #ffffff;`;
      const taskToday = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Bogota' }).format(new Date());
      const taskHorizon = new Date(`${taskToday}T00:00:00Z`);
      taskHorizon.setUTCDate(taskHorizon.getUTCDate() + 2);
      const taskHorizonDate = taskHorizon.toISOString().slice(0, 10);
      const taskCompletionDate = task => {
        if (!task.completed_at) return '';
        const completed = new Date(task.completed_at);
        return Number.isNaN(completed.getTime())
          ? String(task.completed_at).slice(0, 10)
          : new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Bogota' }).format(completed);
      };
      const visibleOperationalTasks = operationalTasks.filter(task => {
        if (task.archived) return false;
        if (task.completed) {
          const completedDate = taskCompletionDate(task);
          return completedDate === taskToday || completedDate === date;
        }
        return task.due_date && String(task.due_date).slice(0, 10) <= taskHorizonDate;
      });
      const taskAssignee = task => task.model_stage_name || task.model_full_name || task.monitor_display_name || task.monitor_full_name || 'Monitor';
      const taskStatus = task => {
        if (task.completed) return 'Completed';
        if (task.due_date < taskToday) return 'Overdue';
        if (task.due_date === taskToday) return 'Due Today';
        return 'Upcoming';
      };
      const escapeHtml = (value = '') => String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
      const renderRichReportText = (value) => {
        const text = value && value !== '-' ? String(value) : '-';
        if (!/<\/?(strong|b|br|div|p)\b/i.test(text)) {
          return escapeHtml(text)
            .replace(/\r?\n/g, '<br>');
        }

        const template = document.createElement('template');
        template.innerHTML = text;
        const cleanNode = (node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            return document.createTextNode(node.textContent || '');
          }
          if (node.nodeType !== Node.ELEMENT_NODE) {
            return document.createTextNode('');
          }
          if (node.tagName === 'DIV' || node.tagName === 'P') {
            const fragment = document.createDocumentFragment();
            Array.from(node.childNodes).forEach((child) => fragment.appendChild(cleanNode(child)));
            fragment.appendChild(document.createElement('br'));
            return fragment;
          }
          if (!['STRONG', 'B', 'BR'].includes(node.tagName)) {
            const fragment = document.createDocumentFragment();
            Array.from(node.childNodes).forEach((child) => fragment.appendChild(cleanNode(child)));
            return fragment;
          }
          const tagName = node.tagName === 'B' ? 'strong' : node.tagName.toLowerCase();
          const clean = document.createElement(tagName);
          Array.from(node.childNodes).forEach((child) => clean.appendChild(cleanNode(child)));
          return clean;
        };
        const output = document.createElement('div');
        Array.from(template.content.childNodes).forEach((child) => output.appendChild(cleanNode(child)));
        return output.innerHTML.replace(/(<br>\s*)+$/g, '') || '-';
      };
      const hexToRgba = (hex, alpha = 0.1) => {
        const raw = String(hex || '').trim().replace('#', '');
        if (!/^[0-9a-fA-F]{6}$/.test(raw)) return `rgba(0, 0, 0, ${alpha <= 0.1 ? 0.04 : alpha})`;
        const int = Number.parseInt(raw, 16);
        const r = (int >> 16) & 255;
        const g = (int >> 8) & 255;
        const b = int & 255;
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      };
      const getSoftTint = () => hexToRgba(headerColor, 0.1);
      const isReviewed = (item, field) => !!item?.timeModified?.[field];
      const getTimeCellStyle = (item, field) => {
        return isReviewed(item, field)
          ? tdStyle
          : `${tdStyle} background-color: ${getSoftTint()};`;
      };
      const formatContinuedTime = (item, field, value) => {
        const displayValue = value || '-';
        return item?.timeContinued?.[field] ? `${displayValue} &rarr;` : displayValue;
      };
      const timeTrackingItems = salesData.filter(
        item => item.arrival !== '-' || item.connection !== '-' || item.logout !== '-' || item.breakTime !== '-' || item.hours !== '-'
      );
      const reviewedTimeTrackingCount = timeTrackingItems.filter((item) => {
        if (isSatellite) {
          return !!(item?.timeModified?.connectionTime && item?.timeModified?.logoutTime);
        }
        return !!(
          item?.timeModified?.arrivalTime &&
          item?.timeModified?.connectionTime &&
          item?.timeModified?.logoutTime &&
          item?.timeModified?.breakTime
        );
      }).length;
      const totalTimeTrackingCount = timeTrackingItems.length;
      const buildSection = (title, body) => `
        <div style="margin-top: 12px;">
          <div style="${sectionWrapStyle}">
            <div style="${subHeaderStyle}; text-align: left;">${title}</div>
            ${body}
          </div>
        </div>
      `;

      return `
        <div style="font-family: Arial, sans-serif; color: #333; max-width: 800px; margin: 0 auto; background: #ffffff;">
          <div style="${sectionWrapStyle}">
            <div style="${headerStyle}">
              ${capitalizeShift} Report&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;${monitor}&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;${studioLabel}&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;${date}
            </div>
          </div>

          ${isSatellite ? `
          ${buildSection('Shift Time', `
                <table style="${tableStyle}">
                  <tbody>
                    <tr>
                      <td style="${tdStyle}; width: 160px; font-weight: 700;">Start</td>
                      <td style="${tdStyle}; width: 240px;">${satelliteShiftStart || '-'}</td>
                      <td style="${tdStyle}; width: 160px; font-weight: 700;">Finish</td>
                      <td style="${tdStyle}; width: 240px;">${satelliteShiftEnd || '-'}</td>
                    </tr>
                    ${hasSecondSatelliteShift ? `
                    <tr>
                      <td style="${tdStyle}; width: 160px; font-weight: 700;">Start 2</td>
                      <td style="${tdStyle}; width: 240px;">${satelliteSecondShiftStart || '-'}</td>
                      <td style="${tdStyle}; width: 160px; font-weight: 700;">Finish 2</td>
                      <td style="${tdStyle}; width: 240px;">${satelliteSecondShiftEnd || '-'}</td>
                    </tr>
                    ` : ''}
                  </tbody>
                </table>
          `)}
          ` : ''}

          ${!isSatellite ? `
          ${buildSection('Assistance', `
                <table style="${tableStyle}">
                  <thead>
                    <tr>
                      <th style="${thStyle}">Model Name</th>
                      <th style="${thStyle}">Stage Name</th>
                      <th style="${thStyle}">${isSatellite ? 'Studio' : 'Room #'}</th>
                      <th style="${thStyle}">Assistance</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${attendance.length > 0 ? attendance.map(item => `
                      <tr>
                        <td style="${tdStyle} width: 240px;">${item.fullName || '-'}</td>
                        <td style="${tdStyle} width: 160px;">${item.stageName || '-'}</td>
                        <td style="${tdStyle}">${isSatellite ? studioMap[item.studio_id] || '-' : item.room || '-'}</td>
                        <td style="${tdStyle}">${item.assistance || '-'}</td>
                      </tr>
                    `).join('') : `<tr><td style="${tdStyle}" colspan="4">No attendance data</td></tr>`}
                  </tbody>
                </table>
          `)}
          ` : ''}

          ${isSatellite ? `
          ${buildSection('Time Tracking Review', `
                <table style="${tableStyle}">
                  <tbody>
                    <tr>
                      <td style="${tdStyle}; width: 240px; font-weight: 700;">Reviewed</td>
                      <td style="${tdStyle};">${reviewedTimeTrackingCount} / ${totalTimeTrackingCount}</td>
                    </tr>
                  </tbody>
                </table>
          `)}
          ` : ''}

          ${buildSection('Time Tracking', `
                <table style="${tableStyle}">
                  <thead>
                    <tr>
                      <th style="${thStyle}">Model Name</th>
                      <th style="${thStyle}">${isSatellite ? 'Stage Name' : 'Arrival'}</th>
                      <th style="${thStyle}">Connection</th>
                      <th style="${thStyle}">Log-Out</th>
                      <th style="${thStyle}">Break</th>
                      <th style="${thStyle}">Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                  ${
                    timeTrackingItems
                      .map(item => `
                        <tr>
                          <td style="${tdStyle} width: 240px;">${item.fullName || '-'}</td>
                          <td style="${getTimeCellStyle(item, 'arrivalTime')}">${isSatellite ? item.stageName || '-' : formatContinuedTime(item, 'arrivalTime', item.arrival)}</td>
                          <td style="${getTimeCellStyle(item, 'connectionTime')}">${formatContinuedTime(item, 'connectionTime', item.connection)}</td>
                          <td style="${getTimeCellStyle(item, 'logoutTime')}">${formatContinuedTime(item, 'logoutTime', item.logout)}</td>
                          <td style="${getTimeCellStyle(item, 'breakTime')}">${item.breakTime || '-'}</td>
                          <td style="${tdStyle}">${item.hours || '-'}</td>
                        </tr>
                      `).join('') || `<tr><td style="${tdStyle}" colspan="6">No time data</td></tr>`
                  }
                </tbody>
                </table>
          `)}

          ${buildSection('Sales', `
                <table style="${tableStyle}">
                  <thead>
                    <tr>
                      <th style="${thStyle}">Stage Name</th>
                      ${salesSites.map(site => `<th style="${thStyle}">${useShortSiteLabels ? site.shortLabel : site.displayName}</th>`).join('')}
                      <th style="${thStyle}">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${salesData.length > 0 ? salesData.map(sale => `
                      <tr>
                        <td style="${tdStyle} width: 240px;">${sale.stageName || '-'}</td>
                        ${salesSites.map(site => `<td style="${tdStyle}">$${parseFloat(sale.earnings?.[site.displayName] || 0).toFixed(2)}</td>`).join('')}
                        <td style="${tdStyle}; background-color: ${
                          sale.goalColor === 'green-bg' ? '#d4edda' :
                          sale.goalColor === 'yellow-bg' ? '#fff3cd' :
                          sale.goalColor === 'red-bg' ? '#f8d7da' : 'transparent'
                        };">
                          $${parseFloat(sale.total || 0).toFixed(2)}
                        </td>
                      </tr>
                    `).join('') : `<tr><td style="${tdStyle}" colspan="${salesSites.length + 2}">No sales data</td></tr>`}
                    <tr>
                      <td style="${tdStyle} font-weight: bold;">Total</td>
                      ${salesSites.map(site => `<td style="${tdStyle}">$${parseFloat(platformTotals[site.displayName] || 0).toFixed(2)}</td>`).join('')}
                      <td style="${tdStyle}; font-weight: bold; background-color: ${
                        (() => {
                          const assistedCount = attendance.filter(m => m.assistance && m.assistance.toLowerCase() === 'yes').length;
                          const red = 29.99 * assistedCount;
                          const yellow = 59.99 * assistedCount;
                          if (grandTotal <= red) return '#f8d7da';
                          if (grandTotal <= yellow) return '#fff3cd';
                          return '#d4edda';
                        })()
                      };">
                        $${parseFloat(grandTotal || 0).toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
          `)}

          ${buildSection('Model Focus', `
                <table style="${tableStyle}">
                  <thead>
                    <tr>
                      <th style="${thStyle}">Model Name</th>
                      <th style="${thStyle}">Actions & Observations</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${modelFocus.length > 0 ? modelFocus.map(item => `
                      <tr>
                       <td style="${item.isModelFocus ? focusedTdStyle : tdStyle} width: 240px;">
                          ${item.focus || '-'}<br>
                          <span style="font-size: 0.75rem; color: #888;">
                            ${item.stageName || '-'}&nbsp;&nbsp;&nbsp;-&nbsp;&nbsp;&nbsp;${isSatellite ? (studioMap[item.studio_id] || '-') : `Room ${item.room || '-'}`}
                          </span>
                        </td>
                        <td style="${item.isModelFocus ? focusedTdStyle : tdStyle}">${renderRichReportText(item.notes)}</td>
                      </tr>
                    `).join('') : `<tr><td style="${tdStyle}" colspan="2">No model focus data</td></tr>`}
                  </tbody>
                </table>
          `)}

          ${!isSatellite ? `
          ${buildSection('Break & Usage Tracking', `
                <table style="${tableStyle}">
                  <thead>
                    <tr>
                      <th style="${thStyle}">Break</th>
                      <th style="${thStyle}">Lush 1</th>
                      <th style="${thStyle}">Lush 2</th>
                      <th style="${thStyle}">Sex Machine</th>
                      <th style="${thStyle}">Cellphone</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${breakItems.length > 0 ? breakItems.map(entry => `
                      <tr>
                        <td style="${tdStyle}">${entry.break || '-'}</td>
                        <td style="${tdStyle}">${entry.lush1 || '-'}</td>
                        <td style="${tdStyle}">${entry.lush2 || '-'}</td>
                        <td style="${tdStyle}">${entry.machine || '-'}</td>
                        <td style="${tdStyle}">${entry.cellphone || '-'}</td>
                      </tr>
                    `).join('') : `<tr><td style="${tdStyle}" colspan="5">No break/usage data</td></tr>`}
                  </tbody>
                </table>
          `)}
          ` : ''}

          ${
            adjustmentList.length > 0 && adjustmentList.some(adj =>
              (adj.description && adj.description.trim() !== '' && adj.description.trim() !== '-') ||
              (parseFloat(adj.amount) > 0)
            )
              ? `
          ${buildSection('Adjustments & Purchases', `
                <table style="${tableStyle}">
                  <thead>
                    <tr>
                      <th style="${thStyle}">Name</th>
                      <th style="${thStyle}">Type</th>
                      <th style="${thStyle}">Amount</th>
                      <th style="${thStyle}">Description</th>
                      <th style="${thStyle}">Quantity</th>
                      <th style="${thStyle}">Quotes</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${adjustmentList
                      .map(adj => `
                        <tr>
                          <td style="${tdStyle} width: 240px;">${adj.name || '-'}</td>
                          <td style="${tdStyle} width: 100px;">${adj.type || '-'}</td>
                          <td style="${tdStyle} width: 100px;">${adj.currency === 'USD' ? '$' : 'COP '}${parseFloat(adj.amount || 0).toFixed(2)}</td>
                          <td style="${tdStyle}">${adj.description || '-'}</td>
                          <td style="${tdStyle} width: 60px;">${adj.quantity || 1}</td>
                          <td style="${tdStyle} width: 50px;">${adj.quotas || '-'}</td>
                        </tr>
                      `)
                      .join('')}
                  </tbody>
                </table>
          `)}` : ''
          }

          ${
            shiftObservations.length > 0
              ? `
          ${buildSection('Shift Observations', `
                <table style="${tableStyle}">
                  <tbody>
                    ${shiftObservations.map(obs => `
                      <tr>
                        <td style="${tdStyle}">${renderRichReportText(obs)}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
          `)}
          ` : ''
          }

          ${visibleOperationalTasks.length > 0 ? `
          ${buildSection('Completed & Pending Tasks', `
                <table style="${tableStyle}">
                  <thead><tr><th style="${thStyle}">Assigned To</th><th style="${thStyle}">Task</th><th style="${thStyle}">Due Date</th><th style="${thStyle}">Status</th></tr></thead>
                  <tbody>
                    ${visibleOperationalTasks.map(task => `
                      <tr style="${task.completed ? 'background:#f1f3f5;color:#737d85;' : ''}">
                        <td style="${tdStyle}">${taskAssignee(task)}</td>
                        <td style="${tdStyle}">${task.completed ? '<span style="color:#238b4b;font-weight:900;margin-right:5px;">&#10004;</span>' : ''}${task.title || '-'}</td>
                        <td style="${tdStyle}">${task.due_date || '-'}</td>
                        <td style="${tdStyle}${task.completed ? 'color:#238b4b;font-weight:800;' : ''}">${taskStatus(task)}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
          `)}
          ` : ''}

          ${!isSatellite ? `
          ${buildSection('Checklist', `
                <ul style="columns: 2; -webkit-columns: 2; -moz-columns: 2; list-style: none; padding-left: 0; margin: 8px 0;">
                  ${checklist.length > 0 ? checklist.map(item => `<li style="margin-bottom: 4px;">
                    <span style="font-size: 22px; display: inline-block; width: 24px;">${item.slice(0, 2)}</span>${item.slice(2)}
                  </li>`).join('') : '<li>No checklist items</li>'}
                </ul>
          `)}
          ` : ''}

          <div style="margin-top: 12px; font-size: 10px; color: #777; text-align: center;">
            Generated on ${new Date().toLocaleString()}
          </div>
        </div>
      `;
    },
  },
  mounted() {
    this.internalReportData = {
      ...this.reportData
    };
  },
  methods: {
    copyToClipboard() {
      // Ensure internalReportData is a deep copy of reportData
      this.internalReportData = JSON.parse(JSON.stringify(this.reportData));

      this.$nextTick(() => {
        const el = this.$refs.reportContent;
        if (!el) {
          console.error("Report content element not found");
          return;
        }

        el.style.display = 'block';
        const range = document.createRange();
        range.selectNodeContents(el);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);

        try {
          document.execCommand("copy");
          this.$emit("copied");
        } catch (err) {
          console.error("Failed to copy:", err?.message || err);
          this.$emit("copy-failed", err);
        }

        selection.removeAllRanges();
        el.style.display = 'none';
      });
    },
  },
};
</script>

<style scoped>
.report-clipboard {
  position: absolute;
  left: -9999px;
}
</style>
 
